import { SharedAppConstants } from '@tock/shared-app-constants';
import { dashboard } from 'types';
import { toEnumString } from '@tock/coretypes';
import _ from 'lodash';
import protos from 'utils/protos';

const {
  DietaryNote,
  GuestProfile,
  GuestProfileSearchRequest,
  GuestProfileMergeRequest,
  GuestProfileSuggestRequest,
  GuestProfileDeleteRequest,
  NoteType,
  PatronNote,
  UpdateTag,
} = protos;

/**
 * Search for patron profiles matching the search criteria
 *
 * @param {Object} request GuestProfileSearchRequest
 * @returns {Object} A Redux TockApi action.
 */
export function search(request: dashboard.IGuestProfileSearchRequest): ApiAction {
  request.matchingString = request.matchingString || '';
  return {
    TockApi: {
      postProto: '/guest/profile/search',
      request: GuestProfileSearchRequest.fromObject(request),
      type: SharedAppConstants.GUEST_PROFILE_SEARCH,
      discardOldRequests: true,
      onError: {
        7003: '',
        defaultToOriginal: true,
      },
    },
  };
}

export function getExportGuestDataDownloadToken(
  request: dashboard.IGuestProfileSearchRequest
): ApiAction {
  request.matchingString = request.matchingString || '';
  return {
    TockApi: {
      postProto: '/guest/profile/search/export',
      request: GuestProfileSearchRequest.fromObject(request),
      type: SharedAppConstants.GUEST_PROFILE_EXPORT,
    },
  };
}

export function suggest(searchText: string): ApiAction {
  return {
    TockApi: {
      postProto: '/guest/profile/suggest',
      request: GuestProfileSuggestRequest.fromObject({ searchText }),
      type: SharedAppConstants.GUEST_PROFILE_SUGGEST,
      discardOldRequests: true,
      onError: {
        7003: '',
        defaultToOriginal: true,
      },
    },
  };
}

/**
 * Get profile for the given patronId
 *
 * @param {Number} patronId Patron's id
 * @returns {Object} A Redux TockApi action.
 */
export function get(patronId: number, businessId?: number): ApiAction {
  return {
    TockApi: {
      getProto: `/guest/profile/${patronId}`,
      type: SharedAppConstants.GUEST_PROFILE_GET,
      context: businessId,
    },
  };
}

/**
 * Get profile for the given patronId for the view modal
 *
 * @param {Number} patronId Patron's id
 * @returns {Object} A Redux TockApi action.
 */
export function view(patronId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/guest/profile/${patronId}`,
      type: SharedAppConstants.GUEST_PROFILE_VIEW,
    },
  };
}

/**
 * Get existing profile for the given email
 *
 * @param {String} email Patron's email
 * @returns {Object} A Redux TockApi action.
 */
export function lookupByEmail(email: string): ApiAction {
  return {
    TockApi: {
      getProto: `/guest/profile/lookup?email=${encodeURIComponent(email)}`,
      type: SharedAppConstants.GUEST_PROFILE_FIND_EXISTING,
      onError: {
        1001: '', // Not found
        defaultToOriginal: true,
      },
    },
  };
}

/**
 * Update the patron ids tags
 *
 * @param {Number} patronId the patron id
 * @param {Array} tagsToAdd the tags to add to the patron
 * @param {Array} tagsToRemove the tags to remove from the patron
 * @returns {Object} A Redux TockApi action.
 */
export function updatePatronTags(
  patronId: number,
  tagsToAdd: string[],
  tagsToRemove: string[],
  successMessageType?: 'TOAST' | 'HIDDEN'
): ApiAction {
  const update = UpdateTag.fromObject({ toAdd: tagsToAdd, toRemove: tagsToRemove });
  return {
    TockApi: {
      postListProto: `/guest/profile/${patronId}/tag`,
      type: SharedAppConstants.PATRON_TAG_UPDATE,
      request: update,
      toast: successMessageType === 'HIDDEN' ? undefined : 'Tags updated',
    },
  };
}

const noteTypeToAjaxInfo = {
  [toEnumString(NoteType, NoteType.DIETARY)]: {
    pathSuffix: '/dietary/note',
    proto: DietaryNote,
    type: SharedAppConstants.GUEST_PROFILE_DIETARY_NOTE_UPDATE,
  },
  [toEnumString(NoteType, NoteType.GENERAL)]: {
    pathSuffix: '/note',
    proto: PatronNote,
    type: SharedAppConstants.GUEST_PROFILE_NOTE_UPDATE,
  },
};

export function setNote(
  patronId: number,
  businessId: number,
  patronNote: string,
  noteType: dashboard.NoteType,
  noteScope: dashboard.NoteScope,
  previousVersionId?: number
): ApiAction {
  const { pathSuffix, proto, type } = noteTypeToAjaxInfo[noteType];
  const note = proto.fromObject({
    note: patronNote,
    noteScope,
    patronId,
    previousVersionId,
  });
  return {
    TockApi: {
      postProto: `/patron${pathSuffix}`,
      type,
      request: note,
      toast: 'Note updated',
      context: { businessId, type: noteType },
    },
  };
}

/**
 * Update the patron ids tags
 *
 * @param {Object} request the proto of the patron as a profile
 * @returns {Object} A Redux TockApi action.
 */
export function update(request: dashboard.IGuestProfile, toast?: string): ApiAction {
  return {
    TockApi: {
      postProto: '/guest/profile',
      type: SharedAppConstants.GUEST_PROFILE_UPDATE,
      request: GuestProfile.fromObject(request),
      toast: _.isUndefined(toast) ? 'Guest profile updated' : '',
    },
  };
}

/**
 * Get a patron's history
 * @param {Object} patronId A patron's id
 * @returns {Object} A Redux TockApi action.
 */
export function fetchAdminPurchaseHistory(patronId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/admin/purchase?patronId=${patronId}`,
      type: SharedAppConstants.GUEST_PROFILE_FETCH_PATRON_PURCHASE_HISTORY,
    },
  };
}

export function selectProfile(profile: dashboard.GuestProfile): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_SELECT_PROFILE,
    payload: profile,
  };
}

export function unselectProfile(profile: dashboard.GuestProfile): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_UNSELECT_PROFILE,
    payload: profile,
  };
}

export function selectAll(): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_SELECT_ALL,
  };
}

export function clearSelected(profilesToRemove?: dashboard.GuestProfile[]): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_CLEAR_SELECTED,
    payload: profilesToRemove,
  };
}

export function mergeGuests(
  request: dashboard.IGuestProfileMergeRequest,
  toast?: string
): ApiAction {
  return {
    TockApi: {
      postProto: '/admin/guests/merge',
      type: SharedAppConstants.GUEST_PROFILE_MERGE_GUESTS,
      request: GuestProfileMergeRequest.fromObject(request),
      toast: toast || 'Guests have been merged',
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateImage(patronId: number, file: any): ApiAction {
  return {
    TockApi: {
      postFile: `/guest/profile/${patronId}/image`,
      request: file,
      type: SharedAppConstants.GUEST_PROFILE_IMAGE_UPDATE,
    },
  };
}

export function clear(): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_CLEAR,
  };
}

export function clearExisting(): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_CLEAR_EXISTING,
  };
}

export function clearView(): BaseTockAction {
  return {
    type: SharedAppConstants.GUEST_PROFILE_CLEAR_VIEW,
  };
}

export function removeProfile(patronId: number): ApiAction {
  return {
    TockApi: {
      deleteProto: `/guest/profile/${patronId}`,
      type: SharedAppConstants.GUEST_PROFILE_DELETE,
    },
  };
}

export function removeProfiles(request: dashboard.IGuestProfileDeleteRequest): ApiAction {
  return {
    TockApi: {
      deleteListProto: `/admin/guests`,
      type: SharedAppConstants.GUEST_PROFILES_DELETE,
      request: GuestProfileDeleteRequest.fromObject(request),
    },
  };
}

export function openMergeGuestProfilesModal(): BaseTockAction {
  return {
    payload: true,
    type: SharedAppConstants.GUEST_PROFILE_TOGGLE_MERGE_GUEST_PROFILES_MODAL,
  };
}

export function closeMergeGuestProfilesModal(): BaseTockAction {
  return {
    payload: false,
    type: SharedAppConstants.GUEST_PROFILE_TOGGLE_MERGE_GUEST_PROFILES_MODAL,
  };
}
